import { useState, useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { uniqueId } from '@utils/uniqueId';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@components/ui';
import Modal from '@components/ui/Modal/Modal';
import AlertModal from '@components/ui/AlertModal/AlertModal';
import ProfileActionsMenu from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import type { ProfileAction } from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import { Forbidden } from '@workpay/ds-shared/components/Forbidden/Forbidden';
import { isForbiddenError } from '@workpay/ds-shared/components/Forbidden/ForbiddenBoundary';
import { serializeFilters } from '@utils/tabHelpers';
import { DraggableTabs } from '@components/shared/DraggableTabs/DraggableTabs';
import type { TabItem } from '@components/shared/DraggableTabs/types';
import { getTenantById, updateTenant, activateTenant, deleteTenant } from '@/services/tenantService';
import { uploadFile } from '@/services/fileService';
import {
  setOrganizationProfileImage,
  clearOrganizationProfileImage,
} from '@/services/organizationService';
import { getTemplateSnapshot } from '@/services/formTemplateService';
import { useNotificationStore } from '@stores/notificationStore';
import { useAuthStore } from '@/stores/authStore';
import { useBreadcrumbOverride } from '@stores/breadcrumbStore';
import type { FormTemplateTab } from '@/types/formTemplate';
import type { UpdateTenantPayload } from '@/types/tenant';
import { WorkpayApiError } from '@lib/apiEnvelope';
import { toDraggableTabItems, getRemovedTabDetails, toApiTabsFromResolved } from '@utils/tabHelpers';
import { useTranslation } from '@/hooks/useTranslation';
import { AuditTrailModal } from '@components/shared/AuditTrailModal';
import { AUDIT_RESOURCE } from '@lib/auditResourceTypes';
import { toTitleCase } from '@utils/textCase';
import HistoryIcon from '@assets/icons/HistoryIcon';
import ProfileCard from './ProfileCard';
import type { ProfileField } from './ProfileCard';
import ProfileEditForm from './ProfileEditForm';
import TabFormsContent from './TabFormsContent';
import ModuleRegistryTab from './ModuleRegistryTab';
import CommercialTermsTab from './CommercialTermsTab';
import type { ProfileMode, ResolvedTab } from './types';
import styles from './TenantProfileView.module.scss';

const MODULE_REGISTRY_TAB_ID = '_module_registry';
const COMMERCIAL_TERMS_TAB_ID = '_commercial_terms';

// ============================================
// HELPERS
// ============================================

const TENANT_STATUS_BADGE_CLASS: Record<string, string> = {
  ACTIVE: 'statusBadgeActive',
  ONBOARDING: 'statusBadgeInfo',
  CONTRACT_REVIEW_PENDING: 'statusBadgeInfo',
  ACTIVATION_PENDING: 'statusBadgeInfo',
  INACTIVE: 'statusBadgeInactive',
};

const TENANT_STATUS_LABEL_KEY: Record<string, [string, string]> = {
  ACTIVE: ['K_STATUS_ACTIVE', 'Active'],
  ONBOARDING: ['K_STATUS_ONBOARDING', 'Onboarding'],
  CONTRACT_REVIEW_PENDING: ['K_STATUS_CONTRACT_REVIEW_PENDING', 'Contract Review'],
  ACTIVATION_PENDING: ['K_STATUS_ACTIVATION_PENDING', 'Activation Pending'],
  INACTIVE: ['K_STATUS_INACTIVE', 'Inactive'],
};

function buildProfileFields(
  tenantData: Awaited<ReturnType<typeof getTenantById>>,
  t: (key: string, fallback: string) => string,
): ProfileField[] {
  const { tenant, user } = tenantData;
  return [
    { label: t('K_LABEL_ORGANISATION_NAME', 'Company Name'), value: tenant.name || '-' },
    { label: t('K_LABEL_SUBDOMAIN', 'Subdomain'), value: tenant.subdomain ? `${tenant.subdomain}.workpay.com` : '-' },
    { label: t('K_LABEL_COUNTRY', 'Country'), value: tenant.country?.name || '-' },
    { label: t('K_LABEL_MOBILE', 'Mobile'), value: user?.mobile || '-' },
    {
      label: t('K_LABEL_PRODUCT_TYPES', 'Product Types'),
      value: tenant.productTypes?.map((pt) => pt.productType).join(', ') || '-',
    },
    { label: t('K_LABEL_FIRST_NAME', 'First Name'), value: user?.firstName || '-' },
    { label: t('K_LABEL_LAST_NAME', 'Last Name'), value: user?.lastName || '-' },
    { label: t('K_LABEL_EMAIL', 'Email'), value: user?.email || '-' },
  ];
}

function toResolvedTabs(apiTabs: FormTemplateTab[]): ResolvedTab[] {
  return apiTabs.map((tab) => ({
    _tabClientId: uniqueId('tab'),
    name: tab.name,
    description: tab.description ?? '',
    order: tab.order,
    forms: (tab.forms ?? []).map((form) => ({
      formConfigId: form.formConfigId,
      formName: form.formName ?? form.formConfigId,
      accessLevel: form.accessLevel,
      isDefault: form.attributes.isDefault,
    })),
  }));
}


// ============================================
// COMPONENT
// ============================================

interface TenantProfileViewProps {
  mode: ProfileMode;
  onBack?: () => void;
  /** When true on first mount, opens the edit modal automatically (used by `?edit=true` from list pages). */
  initialEdit?: boolean;
}

function TenantProfileView({ mode, onBack, initialEdit = false }: TenantProfileViewProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const pathname = useLocation({ select: (l) => l.pathname });
  const setOverride = useBreadcrumbOverride((s) => s.setOverride);
  const { tenantId } = mode;
  const isAdmin = mode.kind === 'admin';
  const [isEditing, setIsEditing] = useState(initialEdit);
  const [editPending, setEditPending] = useState(false);
  const [showActivateConfirm, setShowActivateConfirm] = useState(false);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);

  const activateMutation = useMutation({
    mutationFn: () => activateTenant(tenantId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] });
      void queryClient.invalidateQueries({ queryKey: ['tenants'] });
      void queryClient.invalidateQueries({ queryKey: ['tenantCounts'] });
      setShowActivateConfirm(false);
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
      setShowActivateConfirm(false);
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: () => deleteTenant(tenantId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] });
      void queryClient.invalidateQueries({ queryKey: ['tenants'] });
      void queryClient.invalidateQueries({ queryKey: ['tenantCounts'] });
      setShowDeactivateConfirm(false);
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
      setShowDeactivateConfirm(false);
    },
  });

  // --- Tenant data (admin: includes tabs from snapshot) ---
  const {
    data: tenantData,
    isLoading: tenantLoading,
    isError: tenantError,
    error: tenantErrorObj,
  } = useQuery({
    queryKey: ['tenant', tenantId],
    queryFn: () => getTenantById(tenantId),
  });
  const tenantForbidden = isForbiddenError(tenantErrorObj);

  // Dynamic breadcrumb: show tenant name
  useEffect(() => {
    if (tenantData?.tenant.name) {
      setOverride(pathname, tenantData.tenant.name);
    }
    return () => setOverride(pathname, null);
  }, [tenantData?.tenant.name, pathname, setOverride]);

  // Edit modal renders only when tenant is loaded and not INACTIVE.
  // Computed at the boundary so `?edit=true` deep-link on an INACTIVE record
  // never flashes the modal open between mount and effect.
  const editModalOpen = isEditing && !!tenantData && tenantData.tenant.status !== 'INACTIVE';

  // Form data API needs objectType/objectId derived from the tenant's organization.
  // objectType = organization.organizationType, objectId mapped by type (TENANT → tenant.id, etc.)
  const formDataObjectType = tenantData?.organization?.organizationType ?? '';
  const formDataObjectId = (() => {
    const orgType = tenantData?.organization?.organizationType;
    if (!orgType) return '';
    switch (orgType) {
      case 'TENANT':
        return tenantData?.tenant?.id ?? '';
      // Future entity types can be mapped here (e.g., PARTNER → partner.id)
      default:
        return tenantData?.organization?.id ?? '';
    }
  })();

  // --- Template snapshot (tenant persona only — own snapshot) ---
  const snapshotObjectId = tenantData?.tenant?.id ?? '';
  const {
    data: snapshotData,
    isLoading: snapshotLoading,
  } = useQuery({
    queryKey: ['templateSnapshot', 'TENANT', snapshotObjectId],
    queryFn: () => getTemplateSnapshot('TENANT', snapshotObjectId),
    enabled: !isAdmin && !!snapshotObjectId,
  });

  // Derive tabs from the correct source
  const sourceTabs = isAdmin ? tenantData?.tabs : snapshotData?.tabs;

  // --- Local tab state ---
  const [tabs, setTabs] = useState<ResolvedTab[]>([]);
  const [activeTabId, setActiveTabId] = useState('');
  const [savedTabsJson, setSavedTabsJson] = useState('');
  const [initialized, setInitialized] = useState(false);
  const [removedTabIds, setRemovedTabIds] = useState<Set<string>>(new Set());
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);

  // Sync resolved data into local state once
  const dataReady = isAdmin ? !!tenantData : !!snapshotData;
  if (dataReady && sourceTabs && !initialized) {
    const clientTabs = toResolvedTabs(sourceTabs);
    setTabs(clientTabs);
    setSavedTabsJson(JSON.stringify(toApiTabsFromResolved(clientTabs)));
    // Admin defaults to Module Registry tab; tenant defaults to first form tab,
    // falling back to Commercial Terms (always present for tenants) when no form tabs exist.
    if (isAdmin) {
      setActiveTabId(MODULE_REGISTRY_TAB_ID);
    } else if (clientTabs[0]) {
      setActiveTabId(clientTabs[0]._tabClientId);
    } else {
      setActiveTabId(COMMERCIAL_TERMS_TAB_ID);
    }
    setInitialized(true);
  }
  // Handle case where data loaded but no tabs
  if (dataReady && !sourceTabs && !initialized) {
    if (isAdmin) {
      setActiveTabId(MODULE_REGISTRY_TAB_ID);
    } else {
      setActiveTabId(COMMERCIAL_TERMS_TAB_ID);
    }
    setInitialized(true);
  }

  const liveTabs = useMemo(
    () => tabs.filter((t) => !removedTabIds.has(t._tabClientId)),
    [tabs, removedTabIds],
  );

  const isDirty = useMemo(
    () => liveTabs.length > 0
      ? JSON.stringify(toApiTabsFromResolved(liveTabs)) !== savedTabsJson
      : removedTabIds.size > 0,
    [liveTabs, savedTabsJson, removedTabIds.size],
  );

  const removedTabDetails = useMemo(
    () => getRemovedTabDetails(tabs, removedTabIds),
    [tabs, removedTabIds],
  );

  const activeTab = tabs.find((t) => t._tabClientId === activeTabId);
  const draggableItems = useMemo(() => toDraggableTabItems(tabs), [tabs]);

  // --- Save mutation (admin tab reorder/removal via tenant update) ---
  const saveMutation = useMutation({
    mutationFn: (data: UpdateTenantPayload) => updateTenant(tenantId, data),
  });

  // --- Handlers ---
  const handleTabClick = useCallback((tab: TabItem) => {
    setActiveTabId(tab.id);
  }, []);

  // Tenant-logo save: hits the org-level endpoint (BE V26 — tenants are 1:1 with
  // organization rows; tenant logo is stored on organization.profile_image_path).
  // Two legs: upload bytes → link bucketPath as `path` on the org. Invalidates the
  // tenant query so the new profileImageUrl re-renders.
  const handleProfileImageSave = useCallback(
    async (blob: Blob) => {
      const orgId = tenantData?.organization?.id;
      if (!orgId) {
        throw new Error('Organization not loaded yet — try again in a moment.');
      }
      const file = new File([blob], `profile-${orgId}-${Date.now()}.jpg`, {
        type: blob.type || 'image/jpeg',
      });
      const uploadStarted = performance.now();
      const uploaded = await uploadFile(file, {
        workflow: 'PROFILE_IMAGE',
        objectType: 'ORGANIZATION',
        objectId: orgId,
      });
      if (!uploaded.bucketPath) {
        throw new Error(
          'Upload response missing bucketPath — file-management-service may not be running ' +
            'a recent enough build, or the PROFILE_IMAGE workflow is not configured to use the public bucket.',
        );
      }
      const linkStarted = performance.now();
      const updated = await setOrganizationProfileImage(orgId, uploaded.bucketPath);
      console.log('[ProfileImage upload] organization (tenant logo)', {
        orgId,
        tenantId,
        uploadedFileId: uploaded.id,
        sizeKb: (uploaded.size / 1024).toFixed(1),
        bucketPath: uploaded.bucketPath,
        uploadMs: Math.round(linkStarted - uploadStarted),
        linkMs: Math.round(performance.now() - linkStarted),
        finalProfileImagePath: updated.profileImagePath,
        finalProfileImageUrl: updated.profileImageUrl,
      });
      void queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] });
    },
    [tenantData?.organization?.id, tenantId, queryClient],
  );

  const handleProfileImageRemove = useCallback(async () => {
    const orgId = tenantData?.organization?.id;
    if (!orgId) return;
    await clearOrganizationProfileImage(orgId);
    void queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] });
  }, [tenantData?.organization?.id, tenantId, queryClient]);

  const handleReorder = useCallback((reorderedItems: TabItem[]) => {
    setTabs((prev) => {
      const tabMap = new Map(prev.map((t) => [t._tabClientId, t]));
      return reorderedItems
        .map((item) => tabMap.get(item.id))
        .filter((t): t is ResolvedTab => t !== undefined);
    });
  }, []);

  const handleRemoveTab = useCallback((tab: TabItem) => {
    setRemovedTabIds((prev) => new Set(prev).add(tab.id));
    if (tab.id === activeTabId) {
      const currentIndex = tabs.findIndex((t) => t._tabClientId === tab.id);
      const remaining = tabs.filter(
        (t, i) => !removedTabIds.has(t._tabClientId) && t._tabClientId !== tab.id && i < currentIndex,
      );
      const fallback = remaining[remaining.length - 1]
        ?? tabs.find((t) => !removedTabIds.has(t._tabClientId) && t._tabClientId !== tab.id);
      setActiveTabId(fallback?._tabClientId ?? '');
    }
  }, [activeTabId, tabs, removedTabIds]);

  const handleUndoRemoveTab = useCallback((tab: TabItem) => {
    setRemovedTabIds((prev) => {
      const next = new Set(prev);
      next.delete(tab.id);
      return next;
    });
  }, []);

  const performSave = useCallback(() => {
    const tabsToSave = tabs.filter((t) => !removedTabIds.has(t._tabClientId));
    saveMutation.mutate(
      { tabs: toApiTabsFromResolved(tabsToSave) },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] });
          setTabs(tabsToSave);
          setSavedTabsJson(JSON.stringify(toApiTabsFromResolved(tabsToSave)));
          setRemovedTabIds(new Set());
          setShowSaveConfirm(false);
        },
        onError: (error) => {
          const e = error as WorkpayApiError;
          addNotification({ variant: 'critical', title: t(e.description, e.description) });
        },
      },
    );
  }, [tabs, removedTabIds, saveMutation, queryClient, tenantId]);

  const handleSaveClick = useCallback(() => {
    if (removedTabIds.size > 0) {
      setShowSaveConfirm(true);
    } else {
      performSave();
    }
  }, [removedTabIds.size, performSave]);

  const handleCancelSave = useCallback(() => {
    setShowSaveConfirm(false);
  }, []);

  // --- Profile edit handlers ---
  const handleEditStart = useCallback(() => setIsEditing(true), []);
  const handleEditClose = useCallback(() => {
    setIsEditing(false);
    // Scrub `?edit=true` on close (admin route only — tenant route has no validateSearch).
    if (initialEdit && isAdmin) {
      void navigate({
        to: '/w/dashboard/profile/$tenantId',
        params: { tenantId },
        search: {},
        replace: true,
      });
    }
  }, [initialEdit, isAdmin, navigate, tenantId]);
  const handleEditCancel = handleEditClose;
  const handleEditSaved = handleEditClose;

  // --- Profile fields ---
  const profileFields = useMemo<ProfileField[]>(
    () => (tenantData ? buildProfileFields(tenantData, t) : []),
    [tenantData, t],
  );

  // Stable identity so `CommercialTermsTab`'s memoised consumers don't bust on every parent render.
  const tenantProductTypeIds = useMemo<string[]>(
    () =>
      tenantData?.tenant.productTypes?.map((pt) => pt.id) ??
      tenantData?.tenant.productTypeIds ??
      [],
    [tenantData?.tenant.productTypes, tenantData?.tenant.productTypeIds],
  );

  // --- Loading state ---
  const isLoading = tenantLoading || (!isAdmin && snapshotLoading);
  if (isLoading) {
    return (
      <div className={styles.page}>
        {onBack && (
          <button type="button" className={styles.backLink} onClick={onBack}>
            &larr; Back
          </button>
        )}
        <ProfileCard name="" fields={[]} loading />
        <div className={styles.loadingState}>{t('K_LOADING_TENANT_PROFILE', 'Loading client profile...')}</div>
      </div>
    );
  }

  // --- Forbidden (page-level — detail route can't render anything without the tenant) ---
  if (tenantForbidden) {
    return (
      <div className={styles.page}>
        <Forbidden
          variant="page"
          onAction={onBack}
          actionLabel={onBack ? t('K_BUTTON_GO_BACK', 'Go Back') : undefined}
          hideAction={!onBack}
        />
      </div>
    );
  }

  // --- Error state ---
  if (tenantError || !tenantData) {
    return (
      <div className={styles.page}>
        {onBack && (
          <button type="button" className={styles.backLink} onClick={onBack}>
            &larr; Back
          </button>
        )}
        <div className={styles.errorState}>
          <p>{t('K_FAILED_LOAD_TENANT_PROFILE', 'Failed to load client profile.')}</p>
          <Button variant="outline" size="sm" onClick={onBack}>
            {t('K_BUTTON_GO_BACK', 'Go Back')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {onBack && (
        <button type="button" className={styles.backLink} onClick={onBack}>
          &larr; Back
        </button>
      )}

      {/* Profile Card — always visible */}
      <ProfileCard
        name={tenantData.tenant.name || t('K_LABEL_TENANT', 'Client')}
        fields={profileFields}
        avatarColor="green"
        editableAvatar={{
          currentImageUrl: tenantData.organization?.profileImageUrl ?? null,
          canEdit: hasPermission('ORGANIZATIONS_UPDATE'),
          onSave: handleProfileImageSave,
          onRemove: handleProfileImageRemove,
        }}
        action={
          <div className={styles.actionGroup}>
            {(() => {
              const status = tenantData.tenant.status;
              const badgeClass = styles[TENANT_STATUS_BADGE_CLASS[status] ?? 'statusBadgeInactive'] ?? '';
              const labelEntry = TENANT_STATUS_LABEL_KEY[status];
              const label = labelEntry ? t(labelEntry[0], labelEntry[1]) : toTitleCase(status);
              return <span className={`${styles.statusBadge} ${badgeClass}`}>{label}</span>;
            })()}
            {hasPermission('AUDIT_READ') && tenantId && (
              <button
                type="button"
                className={styles.auditButton}
                onClick={() => setAuditOpen(true)}
              >
                <HistoryIcon />
                <span>{t('K_AUDIT_TITLE', 'Audit trail')}</span>
              </button>
            )}
            <ProfileActionsMenu
              actions={(() => {
                const items: ProfileAction[] = [];
                if (hasPermission('TENANTS_UPDATE') && tenantData.tenant.status !== 'INACTIVE') {
                  items.push({ id: 'edit', label: t('K_EDIT', 'Edit'), onClick: handleEditStart });
                }
                if (hasPermission('TENANTS_ACTIVATE') && isAdmin && (tenantData.tenant.status === 'ONBOARDING' || tenantData.tenant.status === 'ACTIVATION_PENDING')) {
                  items.push({ id: 'activate', label: t('K_BUTTON_ACTIVATE', 'Activate'), onClick: () => setShowActivateConfirm(true), variant: 'primary' });
                }
                if (isAdmin) {
                  items.push({
                    id: 'viewEmployees',
                    label: t('K_BUTTON_VIEW_ALL_EMPLOYEES', 'View All Employees'),
                    onClick: () => {
                      void navigate({
                        to: '/w/employees',
                        search: {
                          tab: 'ALL',
                          filters: serializeFilters({ tenantId: tenantId }),
                        },
                      });
                    },
                  });
                }
                if (isAdmin && hasPermission('TENANTS_DELETE') && tenantData.tenant.status === 'ACTIVE') {
                  items.push({ id: 'deactivate', label: t('K_DEACTIVATE', 'Deactivate'), onClick: () => setShowDeactivateConfirm(true) });
                }
                return items;
              })()}
            />
          </div>
        }
      />

      {/* Edit Modal */}
      <Modal
        isOpen={editModalOpen}
        onClose={handleEditCancel}
        title={t('K_MODAL_EDIT_TENANT', 'Edit Client')}
        size="md"
        footer={
          <div className={styles.editModalFooter}>
            <Button variant="outline" size="sm" onClick={handleEditCancel} disabled={editPending}>
              {t('K_CANCEL', 'Cancel')}
            </Button>
            <Button variant="filled" size="sm" type="submit" form="profile-edit-form" disabled={editPending}>
              {editPending ? t('K_BUTTON_SAVING', 'Saving...') : t('K_SAVE', 'Save')}
            </Button>
          </div>
        }
      >
        {tenantData && (
          <ProfileEditForm
            tenantData={tenantData}
            formId="profile-edit-form"
            onSaved={handleEditSaved}
            onPendingChange={setEditPending}
          />
        )}
      </Modal>

      {/* Toolbar — Save button for admin when tab reorder/removal is dirty */}
      {isAdmin && isDirty && hasPermission('TENANTS_UPDATE') && tenantData.tenant.status !== 'INACTIVE' && (
        <div className={styles.toolbar}>
          <Button
            variant="filled"
            size="sm"
            onClick={handleSaveClick}
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? t('K_BUTTON_SAVING', 'Saving...') : t('K_SAVE', 'Save')}
          </Button>
        </div>
      )}

      {/* Tabs — always rendered. Admin: Module Registry + Commercial Terms + form tabs.
          Tenant: Commercial Terms (read-only) + form tabs. */}
      <>
        <div className={styles.tabBar}>
            {/* Static tabs — not draggable/removable.
                Admin sees Module Registry + Commercial Terms.
                Tenant sees Commercial Terms only (read-only — Edit gated on TENANTS_UPDATE which tenants lack). */}
            {isAdmin && (
              <button
                type="button"
                className={`${styles.staticTab} ${activeTabId === MODULE_REGISTRY_TAB_ID ? styles.staticTabActive : ''}`}
                onClick={() => setActiveTabId(MODULE_REGISTRY_TAB_ID)}
              >
                {t('K_MODULE_REGISTRY', 'Module Registry')}
              </button>
            )}
            <button
              type="button"
              className={`${styles.staticTab} ${activeTabId === COMMERCIAL_TERMS_TAB_ID ? styles.staticTabActive : ''}`}
              onClick={() => setActiveTabId(COMMERCIAL_TERMS_TAB_ID)}
            >
              {t('K_TAB_COMMERCIAL_TERMS', 'Commercial Terms')}
            </button>
            {tabs.length > 0 && (
              <DraggableTabs
                tabs={draggableItems}
                activeTabId={activeTabId}
                onTabClick={handleTabClick}
                onReorder={handleReorder}
                draggable={isAdmin && hasPermission('TENANTS_UPDATE') && tenantData.tenant.status !== 'INACTIVE'}
                removable={isAdmin && hasPermission('TENANTS_UPDATE') && tenantData.tenant.status !== 'INACTIVE'}
                removedTabIds={removedTabIds}
                onRemove={handleRemoveTab}
                onUndoRemove={handleUndoRemoveTab}
              />
            )}
          </div>

          {/* Tab content */}
          <div className={styles.tabContent}>
            {activeTabId === MODULE_REGISTRY_TAB_ID && isAdmin ? (
              <ModuleRegistryTab organizationId={tenantData?.organization?.id ?? ''} />
            ) : activeTabId === COMMERCIAL_TERMS_TAB_ID ? (
              <CommercialTermsTab
                tenantId={tenantId}
                tenantProductTypeIds={tenantProductTypeIds}
              />
            ) : activeTab && !removedTabIds.has(activeTab._tabClientId) ? (
              <TabFormsContent
                forms={activeTab.forms}
                mode={mode}
                objectType={formDataObjectType}
                objectId={formDataObjectId}
              />
            ) : (
              <p className={styles.tabContentPlaceholder}>
                {t('K_SELECT_TAB_PLACEHOLDER', 'Select a tab to view its forms.')}
              </p>
            )}
          </div>
        </>

      {/* Empty space when no tabs — no message */}

      {/* Save confirmation modal — shown when there are removed tabs */}
      <Modal
        isOpen={showSaveConfirm}
        onClose={handleCancelSave}
        title={t('K_MODAL_CONFIRM_CHANGES', 'Confirm Changes')}
        size="sm"
        footer={
          <div className={styles.confirmFooter}>
            <Button variant="outline" size="sm" onClick={handleCancelSave}>
              {t('K_CANCEL', 'Cancel')}
            </Button>
            <Button variant="filled" size="sm" onClick={performSave} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? t('K_BUTTON_SAVING', 'Saving...') : t('K_CONFIRM', 'Confirm')}
            </Button>
          </div>
        }
      >
        <div className={styles.confirmBody}>
          <p>{t('K_MESSAGE_TABS_PERMANENTLY_REMOVED', 'The following tabs will be permanently removed:')}</p>
          <ul className={styles.confirmList}>
            {removedTabDetails.map((tab, i) => (
              <li key={i}>
                {tab.name}{tab.formNames && ` - ${tab.formNames}`}
              </li>
            ))}
          </ul>
        </div>
      </Modal>

      {/* Activate tenant confirmation modal */}
      <AlertModal
        variant="warning"
        title={t('K_MODAL_ACTIVATE_TENANT', 'Activate Client')}
        description={t('K_CONFIRM_ACTIVATE_TENANT', 'Are you sure you want to activate "{name}"? This will change the client status from Onboarding to Active.').replace('{name}', tenantData.tenant.name)}
        isOpen={showActivateConfirm}
        onClose={() => setShowActivateConfirm(false)}
        primaryLabel={t('K_BUTTON_ACTIVATE', 'Activate')}
        onPrimaryClick={() => activateMutation.mutate()}
        loading={activateMutation.isPending}
      />

      {/* Deactivate tenant confirmation modal */}
      <AlertModal
        variant="danger"
        title={t('K_MODAL_DEACTIVATE_TENANT', 'Deactivate Client')}
        description={t('K_CONFIRM_DEACTIVATE_TENANT', 'Are you sure you want to deactivate "{name}"? The client will no longer be active.').replace('{name}', tenantData.tenant.name)}
        isOpen={showDeactivateConfirm}
        onClose={() => setShowDeactivateConfirm(false)}
        primaryLabel={t('K_DEACTIVATE', 'Deactivate')}
        onPrimaryClick={() => deactivateMutation.mutate()}
        loading={deactivateMutation.isPending}
      />

      {tenantId && (
        <AuditTrailModal
          open={auditOpen}
          onClose={() => setAuditOpen(false)}
          filter={{ resourceType: AUDIT_RESOURCE.CLIENT, resourceId: tenantId }}
          subject={tenantData.tenant.name || undefined}
        />
      )}
    </div>
  );
}

export default TenantProfileView;
